import { getLandingEditorApiOrigin, isLandingEditorApiConfigured } from './landing-editor-api';
import { getSupabaseClient } from './supabase';

export interface SiteAssetRecord {
  id: string;
  site_id: string;
  url: string;
  type: string;
  filename: string;
  prompt?: string | null;
  created_at?: string;
  metadata?: Record<string, unknown> | null;
}

export interface GenerateSiteImageRequest {
  siteId: string;
  prompt: string;
  useContext?: boolean;
}

export interface GenerateSiteImageResponse {
  success?: boolean;
  imageUrl?: string;
  filename?: string;
  promptUsed?: string;
  error?: string;
}

export interface SiteVirtualFile {
  path: string;
  kind: 'page' | 'asset';
  name: string;
  updatedAt?: string;
  url?: string;
  sizeBytes?: number;
}

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const supabase = getSupabaseClient();
  if (!supabase) return headers;
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** POST `/api/sites/:siteId/images/generate` — AI image gen + GCS store (Stage 09). */
export async function postGenerateSiteImage(
  request: GenerateSiteImageRequest,
): Promise<GenerateSiteImageResponse> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${origin}/api/sites/${encodeURIComponent(request.siteId)}/images/generate`,
    {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        prompt: request.prompt,
        useContext: request.useContext ?? true,
      }),
    },
  );
  const body = (await response.json()) as GenerateSiteImageResponse;
  if (!response.ok) {
    throw new Error(body.error ?? `Image generation failed (${response.status})`);
  }
  return body;
}

/** GET `/api/sites/:siteId/assets` — list stored site assets. */
export async function getSiteAssets(siteId: string, type?: string): Promise<SiteAssetRecord[]> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const response = await fetch(
    `${origin}/api/sites/${encodeURIComponent(siteId)}/assets${query}`,
    {
      method: 'GET',
      headers,
      credentials: 'include',
    },
  );
  const body = (await response.json()) as { assets?: SiteAssetRecord[]; error?: string };
  if (!response.ok) {
    throw new Error(body.error ?? `Assets fetch failed (${response.status})`);
  }
  return body.assets ?? [];
}

/** GET `/api/sites/:siteId/code` — home page HTML for virtual FS read. */
export async function getSiteHomeCode(siteId: string): Promise<string> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const response = await fetch(`${origin}/api/sites/${encodeURIComponent(siteId)}/code`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Code fetch failed (${response.status})`);
  }
  return response.text();
}

/** Build virtual file listing from assets + default pages. */
export async function listSiteVirtualFiles(siteId: string): Promise<SiteVirtualFile[]> {
  const files: SiteVirtualFile[] = [
    {
      path: 'index.html',
      kind: 'page',
      name: 'Home page',
      updatedAt: new Date().toISOString(),
    },
  ];
  const assets = await getSiteAssets(siteId);
  for (const asset of assets) {
    files.push({
      path: `assets/${asset.filename}`,
      kind: 'asset',
      name: asset.filename,
      url: asset.url,
      updatedAt: asset.created_at,
    });
  }
  return files;
}

export interface CanvasStateSnapshot {
  snapshot?: unknown;
  panels?: unknown;
  camera?: unknown;
  contexts?: unknown;
  updatedAt?: string;
}

/** GET `/api/sites/:siteId/canvas-state` — persisted tldraw snapshot (Stage 10). */
export async function getSiteCanvasState(siteId: string): Promise<CanvasStateSnapshot | null> {
  if (!isLandingEditorApiConfigured()) return null;
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${origin}/api/sites/${encodeURIComponent(siteId)}/canvas-state`,
    { method: 'GET', headers, credentials: 'include' },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const body = (await response.json()) as { error?: string };
    throw new Error(body.error ?? `Canvas state fetch failed (${response.status})`);
  }
  return (await response.json()) as CanvasStateSnapshot;
}

/** PUT `/api/sites/:siteId/canvas-state` — save tldraw snapshot (Stage 10). */
export async function putSiteCanvasState(
  siteId: string,
  state: CanvasStateSnapshot,
): Promise<void> {
  if (!isLandingEditorApiConfigured()) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const origin = getLandingEditorApiOrigin();
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${origin}/api/sites/${encodeURIComponent(siteId)}/canvas-state`,
    {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify(state),
    },
  );
  if (!response.ok) {
    const body = (await response.json()) as { error?: string };
    throw new Error(body.error ?? `Canvas state save failed (${response.status})`);
  }
}
